import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { toCSSSize } from '../../utils/css';
import { useReeferTheme } from '../providers/reeferThemeProvider/reeferThemeProvider';
import { Skeleton } from '../skeleton/skeleton';
import fallback from './assets/fallbackImage.svg';
import { getResponsiveImageAttributes } from './getResponsiveImageAttributes';
import type { ImageProps } from './image.types';

import styles from './image.module.css';

/**
 * Image component used to render images throughout the apps
 */

export function Image({
  altText,
  aspectRatio = 'auto',
  border,
  borderRadius = 'none',
  className,
  'data-testid': testId,
  fallbackSrc = fallback,
  id,
  height = 'auto',
  loading = 'lazy',
  objectFit = 'cover',
  objectPosition = '50% 50%',
  onLoad,
  responsive = false,
  role,
  sizes,
  src,
  srcSet,
  style,
  width = '100%',
}: ImageProps) {
  // An empty string is treated the same as a missing src.
  const hasValidSrc = typeof src === 'string' && src !== '';
  const [imageError, setImageError] = useState(!hasValidSrc);
  const [imgSrc, setImgSrc] = useState(hasValidSrc ? src : fallbackSrc);
  const [imgSrcSet, setImgSrcSet] = useState<string | undefined>(
    hasValidSrc ? srcSet : undefined
  );
  const [imgSizes, setImgSizes] = useState<string | undefined>(
    hasValidSrc ? sizes : undefined
  );
  const theme = useReeferTheme();

  if (borderRadius === 'circular' && height !== width) {
    throw Error(
      'Height and width need to be equivalent to use a circular border radius.'
    );
  }

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImgSrc(fallbackSrc);
    setImgSrcSet(undefined);
    setImgSizes(undefined);
  }, [fallbackSrc]);

  const responsiveImg = useMemo(
    () =>
      responsive
        ? getResponsiveImageAttributes({
            imageSize: sizes ?? 'default',
            src,
          })
        : null,
    [responsive, sizes, src]
  );

  const getBorderRadius = (imgRadius: string): string => {
    let radius = imgRadius;

    if (imgRadius === 'none') {
      radius = 'none';
    }

    if (imgRadius === 'rounded') {
      radius = theme.borderRadius.lg as string;
    }

    if (borderRadius === 'rounded-small') {
      radius = theme.borderRadius.sm as string;
    }

    if (imgRadius === 'circular') {
      radius = '50%';
    }

    return radius;
  };

  useEffect(() => {
    if (responsiveImg && !responsiveImg.invalid) {
      setImgSrc(responsiveImg.imageSrc);
      setImgSrcSet(responsiveImg.srcSet);
      setImgSizes(responsiveImg.sizes);
    }
  }, [responsiveImg]);

  useEffect(() => {
    if (!hasValidSrc) {
      handleImageError();
    }
  }, [hasValidSrc, handleImageError]);

  // if an image is responsive, we need to wait for the correct image source
  if (
    !imageError &&
    responsive &&
    !responsiveImg?.invalid &&
    responsiveImg?.imageSrc !== imgSrc
  ) {
    return <Skeleton.Bone height={height} width={width} />;
  }

  return (
    <div
      className={clsx(className, styles['image'], {
        [styles['image__border']]: border,
      })}
      id={id}
      style={{
        '--image-border-radius': getBorderRadius(borderRadius),
        '--image-height': toCSSSize(height),
        '--image-width': toCSSSize(width),
        ...style,
      }}
    >
      <img
        className={clsx(styles['image_img'])}
        style={{
          '--image-aspect-ratio': aspectRatio,
          '--image-object-fit': objectFit,
          '--image-object-position': objectPosition,
        }}
        alt={imageError ? 'Failed to load this image.' : altText}
        height={height}
        loading={loading}
        onError={handleImageError}
        onLoad={onLoad}
        role={role}
        sizes={imgSizes}
        src={imgSrc}
        srcSet={imgSrcSet}
        data-testid={testId}
        width={width}
      />
    </div>
  );
}
